import { FirebaseOptions, getApp, getApps, initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, SAMLAuthProvider } from 'firebase/auth';
import {
  claimReload,
  clearCachedTenantConfig,
  decideRefreshAction,
  readCachedTenantConfig,
  writeCachedTenantConfig,
} from '../utils/tenantConfigCache';

type TenantStatus = 'active' | 'pending' | 'suspended';

export interface PublicTenantConfig {
  slug: string;
  display_name: string;
  primary_domain: string;
  firebase_project_id?: string | null;
  firebase_config?: (FirebaseOptions & { samlProviderId?: string }) | null;
  status: TenantStatus;
}

const firebaseConfig: FirebaseOptions = {
  apiKey: process.env.FIREBASE_API_KEY || '',
  authDomain: `${process.env.FIREBASE_PROJECT_ID || ''}.firebaseapp.com`,
  databaseURL: `https://${process.env.FIREBASE_PROJECT_ID || ''}-default-rtdb.firebaseio.com`,
  projectId: process.env.FIREBASE_PROJECT_ID || '',
  storageBucket: `${process.env.FIREBASE_PROJECT_ID || ''}.firebasestorage.app`,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.FIREBASE_APP_ID || '',
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let activeTenantConfig: PublicTenantConfig | null = null;

try {
  console.log('Firebase auth required - initializing Firebase...');
  console.log('Firebase config:', {
    ...firebaseConfig,
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'missing',
  });

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.error('Firebase configuration incomplete. Required: FIREBASE_API_KEY, FIREBASE_PROJECT_ID');
    throw new Error('Firebase configuration incomplete');
  }

  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized');

  auth = getAuth(app);
  console.log('Firebase Auth initialized');
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  app = null;
  auth = null;
}

export { auth };
export { app };
export { activeTenantConfig };

export const isFirebaseInitialized = (): boolean => {
  return auth !== null && app !== null;
};

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

function apiBaseUrl(): string {
  const envApiUrl = ((process.env as any)?.API_BACKEND_BASE_URL || '').trim();
  if (envApiUrl && envApiUrl !== 'undefined' && envApiUrl !== 'null') {
    return envApiUrl.endsWith('/api/v1') ? envApiUrl : `${envApiUrl.replace(/\/$/, '')}/api/v1`;
  }
  return '/api/v1';
}

function normalizeFirebaseConfig(tenant: PublicTenantConfig): FirebaseOptions | null {
  const config = tenant.firebase_config;
  if (!config) {
    return null;
  }

  const projectId = config.projectId || tenant.firebase_project_id || undefined;
  if (!config.apiKey || !config.authDomain || !projectId || !config.appId) {
    return null;
  }

  return {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    databaseURL: config.databaseURL || `https://${projectId}-default-rtdb.firebaseio.com`,
    projectId,
    storageBucket: config.storageBucket || `${projectId}.firebasestorage.app`,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
    measurementId: config.measurementId,
  };
}

function getOrCreateFirebaseApp(name: string, config: FirebaseOptions): FirebaseApp {
  if (getApps().some((candidate) => candidate.name === name)) {
    return getApp(name);
  }
  return initializeApp(config, name);
}

function getLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Ask the backend which tenant this host belongs to.
 * Returns null when the backend answers that it does not know the host (404).
 * Throws on any other failure (network error, 5xx, malformed answer).
 */
async function fetchTenantConfig(host: string): Promise<PublicTenantConfig | null> {
  const response = await fetch(`${apiBaseUrl()}/tenants/public-config`, {
    headers: { Accept: 'application/json' },
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Tenant Firebase config lookup failed for ${host}: ${response.status}`);
  }

  const payload = await response.json();
  const tenant = payload?.data as PublicTenantConfig | undefined;
  if (!tenant) {
    throw new Error(`Tenant Firebase config lookup returned no data for ${host}`);
  }
  return tenant;
}

function applyTenantFirebase(tenant: PublicTenantConfig, config: FirebaseOptions): void {
  app = getOrCreateFirebaseApp(`tenant-${tenant.slug}`, config);
  auth = getAuth(app);
  activeTenantConfig = tenant;
}

/**
 * The pane booted from a cached tenant config; check it against the backend.
 *
 * Runs after the UI is already on screen, so it must never throw. This request
 * is also what wakes a sleeping backend while the user is still signing in.
 * If the backend cannot be reached the cached copy stays in use — the user
 * cannot get past the login screen without the backend anyway.
 */
async function refreshTenantConfigInBackground(
  host: string,
  cached: PublicTenantConfig
): Promise<void> {
  let fresh: PublicTenantConfig | null;
  try {
    fresh = await fetchTenantConfig(host);
  } catch (error) {
    console.warn('Tenant config refresh failed; continuing with the cached copy:', error);
    return;
  }

  try {
    const storage = getLocalStorage();
    if (decideRefreshAction(cached, fresh) === 'unchanged' && fresh) {
      // Same tenant and sign-in setup: just pick up cosmetic changes.
      writeCachedTenantConfig(storage, host, fresh);
      activeTenantConfig = fresh;
      return;
    }

    console.warn('Tenant config changed since this pane was last loaded; restarting.');
    if (fresh && fresh.status === 'active' && normalizeFirebaseConfig(fresh)) {
      writeCachedTenantConfig(storage, host, fresh);
    } else {
      // Unknown host, inactive tenant, or incomplete config: the next load
      // must go to the backend and show the regular error.
      clearCachedTenantConfig(storage);
    }
    if (claimReload(getSessionStorage())) {
      window.location.reload();
    }
  } catch (error) {
    console.error('Failed to apply refreshed tenant config:', error);
  }
}

export interface InitializeTenantFirebaseOptions {
  /**
   * Boot from the tenant config this webview saw last time and verify it in
   * the background, so the pane does not wait for a backend cold start.
   * Only the task pane opts in: the sign-in dialog/browser pages keep waiting
   * for the backend, because a background reload there could interrupt a
   * sign-in that is already in flight.
   */
  useCache?: boolean;
}

export async function initializeTenantFirebase(
  options: InitializeTenantFirebaseOptions = {}
): Promise<PublicTenantConfig | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const host = window.location.hostname.toLowerCase();
  if (!host || isLocalHost(host)) {
    return null;
  }

  if (options.useCache) {
    const cached = readCachedTenantConfig<PublicTenantConfig>(getLocalStorage(), host);
    const cachedFirebaseConfig = cached ? normalizeFirebaseConfig(cached) : null;
    if (cached && cachedFirebaseConfig) {
      applyTenantFirebase(cached, cachedFirebaseConfig);
      console.log(`Firebase initialized for tenant: ${cached.slug} (cached config)`);
      void refreshTenantConfigInBackground(host, cached);
      return cached;
    }
  }

  const tenant = await fetchTenantConfig(host);
  if (!tenant) {
    throw new Error(`Tenant Firebase config lookup failed for ${host}: 404`);
  }
  if (tenant.status !== 'active') {
    throw new Error(`Tenant ${tenant.slug} is not active`);
  }

  const tenantFirebaseConfig = normalizeFirebaseConfig(tenant);
  if (!tenantFirebaseConfig) {
    activeTenantConfig = tenant;
    throw new Error(`Tenant ${tenant.slug} has no complete Firebase web config`);
  }

  applyTenantFirebase(tenant, tenantFirebaseConfig);
  writeCachedTenantConfig(getLocalStorage(), host, tenant);
  console.log(`Firebase initialized for tenant: ${tenant.slug}`);
  return tenant;
}

/** Same SAML setup as dashboard `src/config/firebase.ts` — GCIP Microsoft federation. */
export const getSamlProvider = (): SAMLAuthProvider | null => {
  const providerId =
    (activeTenantConfig?.firebase_config?.samlProviderId || process.env.FIREBASE_SAML_PROVIDER_ID || '').trim();
  if (!providerId || typeof providerId !== 'string') {
    console.warn('Missing FIREBASE_SAML_PROVIDER_ID; Microsoft SSO unavailable');
    return null;
  }
  if (!providerId.startsWith('saml.') || providerId === 'saml.provider-id') {
    console.warn('Invalid FIREBASE_SAML_PROVIDER_ID; expected a real saml.* provider from Firebase');
    return null;
  }
  try {
    return new SAMLAuthProvider(providerId);
  } catch (e) {
    console.error('Failed to initialize SAML provider:', e);
    return null;
  }
};
